// Shared draft-reply (identity-request) logic used by both the Mission Control
// ReviewSidebar and the dashboard ApprovalDrawer. Presentation is per-surface;
// this module is logic only.

// A draft-reply approval payload is the email object emitted by the draft-reply
// skill: { to: string[], subject, text, html }. It carries none of the lead /
// finance / extracted-invoice markers, so consumers match it LAST in their
// payload-detection order and render it with an editable subject+text.

export type DraftReplyPayload = {
  to:      string[]
  subject: string
  text:    string
  html:    string
}

export function asDraftReplyPayload(v: unknown): DraftReplyPayload | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (
    !Array.isArray(o.to) ||
    o.to.length === 0 ||
    typeof o.subject !== 'string' ||
    typeof o.text !== 'string'
  ) return null
  return o as unknown as DraftReplyPayload
}

// draftReplyToHTML mirrors skills/cmd/draft-reply/main.go toHTML EXACTLY so an
// edited reply's html has parity with what the skill would have emitted:
// HTML-escape (Go html.EscapeString set: & < > ' " → &amp; &lt; &gt; &#39; &#34;),
// split on blank lines into trimmed <p> paragraphs, single \n → <br>.
function escapeHTMLForReply(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/'/g, '&#39;')
    .replace(/"/g, '&#34;')
}

export function draftReplyToHTML(text: string): string {
  const paragraphs = text.replace(/\r\n/g, '\n').split('\n\n')
  let out = ''
  for (const p of paragraphs) {
    const trimmed = p.trim()
    if (trimmed === '') continue
    out += '<p>' + escapeHTMLForReply(trimmed).replace(/\n/g, '<br>') + '</p>'
  }
  return out
}

// buildDraftReplyEditedPayload constructs the diff-only editedPayload for the
// POST /v1/approvals/{id}/decision body: a key is included only when it differs
// from the original draft, and an edited text always carries a freshly
// regenerated html (parity with what the skill would have emitted). Both
// approval surfaces MUST build their edits through this helper so the wire
// payloads are identical.
export function buildDraftReplyEditedPayload(
  original: DraftReplyPayload,
  edited: { subject: string; text: string },
): Record<string, unknown> {
  const editedPayload: Record<string, unknown> = {}
  if (edited.subject !== original.subject) {
    editedPayload.subject = edited.subject
  }
  if (edited.text !== original.text) {
    editedPayload.text = edited.text
    editedPayload.html = draftReplyToHTML(edited.text)
  }
  return editedPayload
}
